import { DELHI_CORDINATES } from "@Services/constants";
import { validImageTypes } from "../../cubeComponent/constants/globalVariable";
import get from "lodash/get";
import _ from "lodash";
import type { PortalUnits } from "@Actions/Viewer";

export {
  getProjectWorkspacesListPageUrl,
  getProjectRiskManagementListPageUrl,
  getProjectIssuesPageUrl,
  getProjectFilesPageUrl,
  getProjectTransmittalsPageUrl,
  getSingleTransmittalPageUrl,
  getProjectTasksPageUrl,
  getProjectEstimatesPageUrl,
  getEstimatePageUrl,
  getProjectSchedulesPageUrl,
  getProjectFormsPageUrl,
  getProjectDashboardPageUrl,
  getPortalProjectsUrl,
  getUserProjects,
  getProjectMailboxPageUrl,
  getProjectReviewsPageUrl,
  getProjectContractPageUrl,
  getReviewUrl,
  getProjectGeneralClashPageUrl,
  getProjectTemplatesUrl,
  getClashDetailsPageUrl,
  getCreateTaskPageUrl,
  getEditTaskPageUrl,
  getEstimateUrl,
  getScheduleUrl,
  getContractListUrl,
  getProjectSettingsPageUrl,
  getContractDetailUrl,
  getFormsListUrl,
  getProjectPageUrl,
  getProjectAssetsPageUrl,
} from "@Features/projects/utils/projectUrls";

export const getCustomModulePageUrl = (moduleId: string, selectedTab: string) =>
  `/portalSettings/formsAndFields?selectedCategory=${moduleId}&selectedTab=${selectedTab}`;

export const getAllNotificationPageUrl = ({
  section,
  module,
  ticketId,
  supportType,
  referrerUrl,
}: {
  section?: string;
  module?: string;
  ticketId?: string;
  supportType?: string;
  referrerUrl?: string;
}) => {
  const searchParams = new URLSearchParams("");

  const url = `/notifications`;
  if (section) {
    searchParams.set("section", section);
  }
  if (supportType) {
    searchParams.set("supportType", supportType);
  }
  if (ticketId) {
    searchParams.set("ticketId", ticketId);
  }
  if (module) {
    searchParams.set("module", module);
  }
  if (referrerUrl) {
    searchParams.set("referrerUrl", referrerUrl);
  }

  return `${url}?${searchParams.toString()}`;
};

export const getPortalSettingsTabUrl = (tab: string) => `/portalSettings/${tab}`;
export const getEscrowFilesPageUrl = (batchId: string) => `/escrowFiles/${batchId}`;
export const getPortalFilesUrl = () => `/Files?byPortal=true`;
export const getGroupDetailsUrl = (groupId?: string) => {
  if (groupId) {
    return `/portalSettings/groups/${groupId}`;
  } else {
    return `/portalSettings/groups`;
  }
};

export const getChecklistDetailUrl = (checklistId?: string) => {
  if (checklistId) {
    return `/portalSettings/reviewFlowsTemplate/checklists/${checklistId}`;
  }
  return `/portalSettings/reviewFlowsTemplate/checklists`;
};

export const getTemplateURL = (props: { selectedCategory?: string; selectedTab?: string }) => {
  const { selectedCategory, selectedTab } = props;
  const url = `/portalSettings/formsAndFields`;
  const searchParams = new URLSearchParams();

  if (selectedCategory) {
    searchParams.set("selectedCategory", selectedCategory.toLowerCase());
  }
  if (selectedTab) {
    searchParams.set("selectedTab", selectedTab);
  }
  return `${url}?${searchParams.toString()}`;
};
export const getFormsTemplateUrl = (props: { templateId?: string; resourceType?: string }) => {
  const { templateId, resourceType } = props;
  return `/portalSettings/formsAndFields/${templateId}/${resourceType?.toLowerCase()}`;
};
export const getAccessTemplateUrl = (props: { templateId?: string }) => {
  const { templateId } = props;
  return `/portalSettings/accessTemplates/${templateId}`;
};

export const getPortalOrgUsersUrl = (orgId: string) => `/portalSettings/organisations/${orgId}/orgUsers`;
export const getPortalOrganizationUrl = "/portalSettings/organisations";

export const getNomenclatureTemplatesUrl = () => `/portalSettings/nomenclature`;
export const getNomenclatureTemplateUrl = (templateId: string) => `/portalSettings/nomenclature/template/${templateId}`;

export const chr = (codePt: number) => {
  if (codePt > 0xffff) {
    codePt -= 0x10000;
    return String.fromCharCode(0xd800 + (codePt >> 10), 0xdc00 + (codePt & 0x3ff));
  }
  return String.fromCharCode(codePt);
};

// Callers pass either an alpha symbol or an already-numeric value, hence the union rather than
// `string`; it is normalised to its uppercased string form before the lookup.
export const charToNum = (alpha: string | number) => {
  const symbol = String(alpha).toUpperCase();
  for (let i = 0; ; i++) {
    if (symbol == numToChar(i)) {
      return i;
    }
  }
};

export const numToChar = (number: number): string => {
  const numeric = (number - 1) % 26;
  const letter = String.fromCodePoint(65 + numeric);
  const number2 = Math.floor((number - 1) / 26);
  if (number2 > 0) {
    return numToChar(number2) + letter;
  }
  return letter;
};

export const toRoman = (number: number): string => {
  let num = Math.floor(number);
  let val;
  let s = "";
  let i = 0;
  const v = [1000, 900, 500, 400, 100, 90, 50, 40, 10, 9, 5, 4, 1];
  const r = ["M", "CM", "D", "CD", "C", "XC", "L", "XL", "X", "IX", "V", "IV", "I"];

  function toBigRoman(n: number): string {
    let ret = "";
    let rem = n;
    while (rem > 1000) {
      let prefix = "";
      let suffix = "";
      let n = rem;
      const s = `${rem}`;
      let magnitude = 1;
      while (n > 1000) {
        n /= 1000;
        magnitude *= 1000;
        prefix += "(";
        suffix += ")";
      }
      const n1 = Math.floor(n);
      rem = Number(s) - n1 * magnitude;
      ret += prefix + toRoman(n1) + suffix;
    }
    return ret + toRoman(rem);
  }

  if (number - num || num < 1) num = 0;
  if (num > 3999) return toBigRoman(num);

  while (num) {
    val = v[i] ?? 0;
    if (!val) {
      break;
    }
    while (num >= val) {
      num -= val;
      s += r[i] ?? "";
    }
    ++i;
  }
  return s;
};

export const fromRoman = (roman: string, accept?: boolean): number => {
  const s = roman.toUpperCase().replace(/ +/g, "");
  const L = s.length;
  let sum = 0;
  let i = 0;
  let next;
  let val;
  const R: { [key: string]: number } = {
    M: 1000,
    D: 500,
    C: 100,
    L: 50,
    X: 10,
    V: 5,
    I: 1,
  };

  function fromBigRoman(rn: string): number {
    let n = 0;
    let x;
    let n1;
    let S;
    const rx = /(\(*)([MDCLXVI]+)/g;

    while ((S = rx.exec(rn)) != null) {
      const group = S[1] ?? "";
      const numeral = S[2] ?? "";
      x = group.length;
      n1 = fromRoman(numeral);
      if (isNaN(n1)) return NaN;
      if (x) n1 *= Math.pow(1000, x);
      n += n1;
    }
    return n;
  }

  if (/^[MDCLXVI)(]+$/.test(s)) {
    if (s.indexOf("(") == 0) return fromBigRoman(s);

    while (i < L) {
      val = R[s.charAt(i++)] ?? 0;
      next = R[s.charAt(i)] || 0;
      if (next - val > 0) val *= -1;
      sum += val;
    }
    if (accept || toRoman(sum) === s) return sum;
  }
  return NaN;
};

/** Numbering-system code stored on a BOQ/WBS column: 0 = plain, 1/2 = alpha, 3/4 = roman. */
type NumberSystem = number;

function getNumberFromNumberSystem(value: string | number, fromNumberSystem: NumberSystem): number {
  if ([1, 2].includes(fromNumberSystem)) {
    return charToNum(value);
  }

  if ([3, 4].includes(fromNumberSystem)) {
    // `fromRoman` uppercases its argument, so it needs the string form of a numeric symbol.
    return fromRoman(String(value));
  }

  // 0 = plain, and any code outside the documented 0-4 range: the value is already the number.
  return Number(value);
}

export function getSymbolForNumber(
  number: string | number,
  toNumberSystem: NumberSystem,
  fromNumberSystem?: NumberSystem,
) {
  // The source value may be an alpha/roman symbol; `numToChar`/`toRoman` below both need the
  // numeric ordinal, which is the coercion the converters used to perform implicitly.
  let convertedNumber = Number(number);

  if (fromNumberSystem) {
    convertedNumber = getNumberFromNumberSystem(number, fromNumberSystem);
  }

  if (toNumberSystem == 1) {
    return numToChar(convertedNumber).toLowerCase();
  }

  if (toNumberSystem == 2) {
    return numToChar(convertedNumber).toUpperCase();
  }

  if (toNumberSystem == 3) {
    return toRoman(convertedNumber).toLowerCase();
  }

  if (toNumberSystem == 4) {
    return toRoman(convertedNumber).toUpperCase();
  }

  return number;
}

export function getNumberBasedOnSettings({
  value,
  decimalPlaces,
  digitGrouping,
  largeNumber,
}: {
  value: number;
  decimalPlaces: number;
  digitGrouping: string;
  largeNumber: string;
}) {
  if (!value) {
    return value;
  }

  let symbol;
  let factor = 1;
  if (largeNumber === "KMB") {
    if (value >= 1000000000) {
      factor = 1000000000;
      symbol = "B";
    } else if (value >= 1000000) {
      factor = 1000000;
      symbol = "M";
    } else if (value >= 1000) {
      factor = 1000;
      symbol = "K";
    }
  } else if (largeNumber === "LCr") {
    if (value >= 10000000) {
      factor = 10000000;
      symbol = "Cr";
    } else if (value >= 100000) {
      factor = 100000;
      symbol = "L";
    }
  }

  value = value / factor;
  let returnValue: string | number = parseFloat(value.toFixed(decimalPlaces));

  if (["Indian", "International"].includes(digitGrouping)) {
    const style = digitGrouping === "Indian" ? "en-IN" : "en-US";
    returnValue = new Intl.NumberFormat(style, { maximumFractionDigits: decimalPlaces }).format(value);
  } else {
    returnValue = String(returnValue);
  }

  if (symbol) {
    returnValue = returnValue + symbol;
  }
  return returnValue;
}

export const prepareEDN = (projectCustomId: string | number | undefined, indexAtProjectLevel: number | undefined) =>
  `${projectCustomId}-${indexAtProjectLevel}`;

/** The avatar-form state this validator writes its result back into. */
interface AvatarFormState {
  logoObj?: File;
  error: { logo: string };
}

export const validateAvatar = (e: { target?: { files?: FileList | null } }, stateData: AvatarFormState) => {
  const file = e.target?.files;
  const data = stateData;
  const selected = file?.[0];
  if (selected && validImageTypes.includes(selected.type)) {
    data.logoObj = selected;
    data.error.logo = "";
  } else {
    data.error.logo = "Please select jpg, jpeg, png file only.";
  }
  return data;
};

export const getTargetValue = (e: { value: unknown }) => e.value;

export const getSelectedIndex = (list: { value?: unknown }[] | undefined, compareTo: unknown) => {
  let indexReturn = -1;
  list?.map((s, index: number) => {
    if (s.value === compareTo) {
      indexReturn = index;
    }
  });
  return indexReturn;
};

export function getLocationCoordinates() {
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const locationCoordinates = [position.coords.latitude, position.coords.longitude];
        resolve(locationCoordinates);
      },
      () => {
        resolve([DELHI_CORDINATES.lat, DELHI_CORDINATES.lng]);
      },
    );
  });
}

let recaptchaLoadPromise: Promise<void> | null = null;

/**
 * `ready` queues a callback until the reCAPTCHA API is usable. It is missing from the ambient
 * `Window["grecaptcha"]` shape — react-app-env.d.ts declares only the `execute` the signup screens
 * call — so the member is checked here instead of read off a shape that does not declare it. The
 * source fix is one line in that ambient declaration: `ready(callback: () => void): void;`.
 */
const hasRecaptchaReady = (value: unknown): value is { ready: (callback: () => void) => void } =>
  typeof value === "object" && value !== null && "ready" in value && typeof value.ready === "function";

export function loadRecaptchaScripts(onReady?: () => void) {
  if (!process.env.REACT_APP_GOOGLE_RECAPTCHA_KEY) {
    onReady?.();
    return;
  }
  document.body.classList.remove("recaptcha-hidden");
  if (!recaptchaLoadPromise) {
    recaptchaLoadPromise = new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_GOOGLE_RECAPTCHA_KEY}`;
      script.async = false;
      script.onload = () => {
        const style = document.createElement("style");
        style.innerHTML = `.grecaptcha-badge { left: 0.25rem !important; right: auto !important; width: 4.375rem !important; overflow: hidden !important; transition: all 0.3s ease !important; } .grecaptcha-badge:hover { width: 16rem !important; } body.recaptcha-hidden .grecaptcha-badge { visibility: hidden !important; }`;
        document.head.appendChild(style);
        const recaptcha: unknown = window.grecaptcha;
        if (hasRecaptchaReady(recaptcha)) {
          // `ready` invokes its callback with no arguments, so the wrapper is what `resolve` received before.
          recaptcha.ready(() => resolve());
        }
      };
      document.body.appendChild(script);
    });
  }
  if (onReady) {
    recaptchaLoadPromise.then(onReady);
  }
}

export function hideRecaptchaBadge() {
  if (!process.env.REACT_APP_GOOGLE_RECAPTCHA_KEY) return;
  document.body.classList.add("recaptcha-hidden");
}

export function trackSignUp(method: string) {
  window.gtag?.("event", "sign_up", { method });
}

const generateId = (field: string) => field.replaceAll(/[^a-zA-Z0-9]/g, "_");

/**
 * One entry of the distinct-file-properties list. Callers pass either the bare dotted field name
 * as a string, or an object carrying it — hence the union.
 */
type DistinctPropertyValue = string | { field?: string; label?: string; [key: string]: unknown };

/**
 * A node of the property tree these builders emit. The extra members come from the caller-supplied
 * `eachColumnMetadata` / `appendTo*Nodes` spreads, so the shape is open.
 */
type PropertyTreeNode = {
  field: string;
  label: string;
  id: string;
  columns?: PropertyTreeNode[];
  isChecked?: boolean;
  parentId?: string | null;
  [key: string]: unknown;
};

/** Caller-supplied members spread onto every emitted node. */
type NodeExtras = object;

/** The dotted field name an entry contributes: its own string form, or the object's `field`. */
const getRawField = (value: DistinctPropertyValue) => (typeof value === "string" ? value : value.field);

/**
 * The dotted path the tree is split on. `overrideField` callers key the hierarchy off `label`
 * instead of `field`. Undefined when the entry carries neither — it cannot be placed in the tree.
 */
const getName = (value: DistinctPropertyValue, overrideField: boolean) => {
  if (typeof value === "string") return value;
  return overrideField ? value.label : value.field;
};

const ensureRootNode = (
  rootNode: string,
  nodeMap: Map<string, PropertyTreeNode>,
  columnData: PropertyTreeNode[],
  appendToNonLeafNodes: NodeExtras,
  eachColumnMetadata: NodeExtras,
) => {
  let rootNodeRef = nodeMap.get(rootNode);

  if (!rootNodeRef) {
    let label = "Metadata";
    if (rootNode === "customProperties") label = "Custom Metadata";
    else if (rootNode === "typeProperties") label = "Type Metadata";

    rootNodeRef = {
      field: rootNode,
      label,
      id: generateId(rootNode),
      columns: [] as PropertyTreeNode[],
      ...appendToNonLeafNodes,
      ...eachColumnMetadata,
    };

    columnData.push(rootNodeRef);
    nodeMap.set(rootNode, rootNodeRef);
  }

  return rootNodeRef;
};

const createNode = (
  fullField: string,
  part: string,
  isLeaf: boolean,
  selectedNodes: string[],
  appendToLeafNodes: NodeExtras,
  appendToNonLeafNodes: NodeExtras,
  eachColumnMetadata: NodeExtras,
): PropertyTreeNode => ({
  field: fullField,
  label: part,
  id: generateId(fullField),
  isChecked: !!selectedNodes.includes(fullField),
  ...(isLeaf ? appendToLeafNodes : appendToNonLeafNodes),
  ...eachColumnMetadata,
});

const buildHierarchy = (
  nameArray: string[],
  rawField: string,
  rootNode: string,
  rootNodeRef: PropertyTreeNode,
  options: {
    nodeMap: Map<string, PropertyTreeNode>;
    selectedNodes: string[];
    appendToLeafNodes: NodeExtras;
    appendToNonLeafNodes: NodeExtras;
    eachColumnMetadata: NodeExtras;
  },
) => {
  const { nodeMap, selectedNodes, appendToLeafNodes, appendToNonLeafNodes, eachColumnMetadata } = options;

  let currentPath = rootNode;
  let parentNode = rootNodeRef;

  for (let i = 0; i < nameArray.length; i++) {
    const part = nameArray[i];
    if (!part) {
      continue;
    }
    const isLeaf = i === nameArray.length - 1;
    const fullField = isLeaf ? rawField : `${currentPath}.${part}`;

    let nodeRef = nodeMap.get(fullField);

    if (!nodeRef) {
      nodeRef = createNode(
        fullField,
        part,
        isLeaf,
        selectedNodes,
        appendToLeafNodes,
        appendToNonLeafNodes,
        eachColumnMetadata,
      );

      if (!isLeaf) nodeRef.columns = [];
      // `columns` is seeded on every non-leaf node (by ensureRootNode and the line above), and
      // only non-leaf nodes ever become `parentNode`. The `!` is erased.
      parentNode.columns!.push(nodeRef);
      nodeMap.set(fullField, nodeRef);
    }

    if (!isLeaf) {
      parentNode = nodeRef;
      currentPath = fullField;
    }
  }
};

const processValue = (
  value: DistinctPropertyValue,
  options: {
    nodeMap: Map<string, PropertyTreeNode>;
    columnData: PropertyTreeNode[];
    selectedNodes: string[];
    appendToLeafNodes: NodeExtras;
    appendToNonLeafNodes: NodeExtras;
    eachColumnMetadata: NodeExtras;
    overrideField: boolean;
  },
) => {
  const {
    nodeMap,
    columnData,
    selectedNodes,
    appendToLeafNodes,
    appendToNonLeafNodes,
    eachColumnMetadata,
    overrideField,
  } = options;

  const rawField = getRawField(value);
  const name = getName(value, overrideField);
  // An entry with neither `field` nor `label` has no path to place it under; skip it rather than
  // splitting `undefined` and taking the whole tree down with it.
  if (!rawField || !name) return;

  // `split` always yields at least one element; the `!` only tells noUncheckedIndexedAccess that.
  const rootNode = name.split(".")[0]!;
  const nameArray = name.split(".").slice(1).join(".").split("/");

  const rootNodeRef = ensureRootNode(rootNode, nodeMap, columnData, appendToNonLeafNodes, eachColumnMetadata);

  const buildHierarchyOptions = {
    nodeMap,
    selectedNodes,
    appendToLeafNodes,
    appendToNonLeafNodes,
    eachColumnMetadata,
  };

  buildHierarchy(nameArray, rawField, rootNode, rootNodeRef, buildHierarchyOptions);
};

export const getTreeViewOfDistinctFileProperties = (
  data: DistinctPropertyValue[],
  eachColumnMetadata: NodeExtras = {},
  selectedNodes: string[] = [],
  appendToLeafNodes: NodeExtras = {},
  appendToNonLeafNodes: NodeExtras = {},
  overrideField = false,
) => {
  const columnData: PropertyTreeNode[] = [];
  const nodeMap = new Map<string, PropertyTreeNode>();

  const processOptions = {
    nodeMap,
    columnData,
    selectedNodes,
    appendToLeafNodes,
    appendToNonLeafNodes,
    eachColumnMetadata,
    overrideField,
  };

  for (const value of data) {
    processValue(value, processOptions);
  }

  return columnData;
};

/** A react-awesome-query-builder field definition, keyed by the property's dotted field name. */
interface QueryBuilderField {
  label?: string;
  type?: string;
}

export const generateQueryBuilderConfig = (array: { label: string; fieldType?: string; questionValue?: string }[]) => {
  const config: Record<string, QueryBuilderField> = {};

  array.forEach(({ label, fieldType, questionValue }) => {
    const prefix = label.split(".")[0];
    const finalFieldName = label.split(".").slice(1).join(".").split("/").pop();

    const fieldDefinition: QueryBuilderField = {
      label: finalFieldName,
      type: "text",
    };
    if (prefix === "customProperties") {
      fieldDefinition.type = fieldType;
      fieldDefinition.label = questionValue;
    }
    config[label] = fieldDefinition;
  });
  return config;
};

/**
 * Produces flat tree data with parentId references - O(n) time, O(n) space
 * Use this instead of getTreeViewOfDistinctFileProperties when passing to TreeViewComponent
 * which now handles flat-to-hierarchical conversion internally
 */
export const getFlatTreeViewOfDistinctFileProperties = (
  data: DistinctPropertyValue[],
  eachColumnMetadata: NodeExtras = {},
  selectedNodes: string[] = [],
  appendToLeafNodes: NodeExtras = {},
  appendToNonLeafNodes: NodeExtras = {},
  overrideField = false,
): PropertyTreeNode[] => {
  if (data.length === 0) return [];

  const flatData: PropertyTreeNode[] = [];
  const nodeMap = new Map<string, PropertyTreeNode>();

  for (const value of data) {
    const rawField = getRawField(value);
    const name = getName(value, overrideField);
    // An entry with neither `field` nor `label` has no path to place it under; skip it rather than
    // splitting `undefined` and taking the whole tree down with it.
    if (!rawField || !name) continue;

    // `split` always yields at least one element; the `!` only tells noUncheckedIndexedAccess that.
    const rootNode = name.split(".")[0]!;
    const nameArray = name.split(".").slice(1).join(".").split("/");

    // Ensure root node exists
    if (!nodeMap.has(rootNode)) {
      let label = "Metadata";
      if (rootNode === "customProperties") label = "Custom Metadata";
      else if (rootNode === "typeProperties") label = "Type Metadata";

      const rootNodeData = {
        field: rootNode,
        label,
        id: generateId(rootNode),
        parentId: null,
        ...appendToNonLeafNodes,
        ...eachColumnMetadata,
      };
      nodeMap.set(rootNode, rootNodeData);
      flatData.push(rootNodeData);
    }

    // Build path nodes
    let currentPath = rootNode;
    let parentId = generateId(rootNode);

    for (let i = 0; i < nameArray.length; i++) {
      const part = nameArray[i];
      if (!part) continue;

      const isLeaf = i === nameArray.length - 1;
      const fullField = isLeaf ? rawField : `${currentPath}.${part}`;
      const nodeId = generateId(fullField);

      if (!nodeMap.has(fullField)) {
        const nodeData = {
          field: fullField,
          label: part,
          id: nodeId,
          parentId: parentId,
          isChecked: !!selectedNodes.includes(fullField),
          ...(isLeaf ? appendToLeafNodes : appendToNonLeafNodes),
          ...eachColumnMetadata,
        };
        nodeMap.set(fullField, nodeData);
        flatData.push(nodeData);
      }

      if (!isLeaf) {
        parentId = nodeId;
        currentPath = fullField;
      }
    }
  }

  return flatData;
};
export const fileToBase64 = async (file: Blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (e) => reject(e);
  });

/** An rc-dock layout node: either a box with `children` or a panel with `tabs`. */
interface RcDockNode {
  children?: RcDockNode[];
  tabs?: { id?: string }[];
  activeId?: string;
}

export function removingElementFromRcDock(child: RcDockNode, nonActiveIds: (string | undefined)[]) {
  // `(child.children?.length)!` keeps the `> 0` comparison verbatim; the assertion is erased.
  if (child.children?.length! > 0) {
    // Parameter name kept verbatim — it shadows the outer `child` in the original.
    // The `!` restates the optional-chained guard above; both assertions are erased.
    child.children = child.children!.filter((child) => removingElementFromRcDock(child, nonActiveIds));
  }
  if (child.tabs) {
    if (child.tabs.length > 1) {
      child.tabs = child.tabs.filter((tab) => !nonActiveIds.includes(tab.id));
      if (nonActiveIds.includes(child.activeId)) {
        if (!child.tabs[0]?.id) {
          return false;
        }
        // Guarded by the `child.tabs[0]?.id` check on the line above; the `!` is erased.
        child.activeId = child.tabs[0]!.id;
      }
      return true;
    }
    if (nonActiveIds.includes(child.activeId)) {
      return false;
    }
    return true;
  }
  return true;
}

export const getFileListPageUrl = (searchParams: string) => {
  const url = new URLSearchParams();
  if (searchParams) {
    url.set("search", searchParams);
  }
  return `/Files?byPortal=true&${url.toString()}`;
};

export const getReviewFlowPageUrl = (typeReviewFlow: string) => {
  const url = new URLSearchParams(window.location.search);
  url.set("type", typeReviewFlow);

  const currentPath = window.location.pathname;
  return `${currentPath}?${url.toString()}`;
};

export function isValidJSON(str: string) {
  try {
    JSON.parse(str);
    return true;
  } catch (_error) {
    return false;
  }
}

export const getReviewPageUrl = (typeReview: string, currentSearchParams: string, currentPathname: string) => {
  const urlParams = new URLSearchParams(currentSearchParams);
  urlParams.set("type", typeReview);

  return `${currentPathname}?${urlParams.toString()}`;
};

/** A form-template section as far as the custom-field harvesters here are concerned. */
interface AnsweredSection {
  questionFields?: { _id?: string; answer?: { value?: unknown } }[];
}

// The default `[]` previously typed this parameter `never[]`, which rejected every real caller.
export const getCustomFields = (formTemplate: AnsweredSection[] = []) => {
  const customFields: Record<string, unknown> = {};
  formTemplate.forEach((section) => {
    section.questionFields?.forEach((field) => {
      if (field.answer && field.answer.value) {
        customFields[field._id!] = field.answer.value;
      }
    });
  });
  return customFields;
};

/** A folder's file-naming rule set (`folders.fileNomenclature`). */
interface FileNomenclature {
  separator?: string;
  fields?: {
    type?: string;
    /** Legacy exact length, superseded by the min/max pair below. */
    allowedCharacters?: number;
    minCharacters?: number | null;
    maxCharacters?: number | null;
    options?: string[];
  }[];
}

export const isFileNomenclatureCorrect = (name: string | undefined, fileNomenclature: FileNomenclature = {}) => {
  // Starts as the raw name and is rewritten into the separator-split segment list below, so the
  // declared type is the union of both forms; each read is asserted to the form it expects.
  let fileName: string | string[] = name || "";
  if (!name || !fileNomenclature.fields) {
    return true;
  }
  if (name.includes(".")) {
    fileName = name.split(".");
    (fileName as string[]).pop();
  } else {
    fileName = [fileName as string];
  }
  fileName = (fileName as string[]).join(".").split(fileNomenclature.separator!);

  const fields = fileNomenclature.fields;
  if (!fields.length) {
    return true;
  }

  const separator = fileNomenclature.separator;

  return fields.every((rule, index: number) => {
    const isLastField = index == fields.length - 1;

    if (isLastField) {
      let lastMax = rule.maxCharacters;
      if (rule.minCharacters == null && lastMax == null && rule.allowedCharacters) lastMax = rule.allowedCharacters;
      const lastFreeForm = rule.type == "variable" || (rule.type == "text" && lastMax == null);
      if (!lastFreeForm && fileName.length !== fields.length) return false;
    }

    if (rule.type == "single select") {
      // Ignore blank option slots (a new single select starts with empty
      // placeholders); an all-blank single select imposes no constraint.
      const options = (rule.options ?? []).filter((option: string) => option !== "");
      if (!options.length) return true;
      // A name with fewer segments than rules yields `undefined` here, and `includes(undefined)`
      // returning false IS the intent — so the ARRAY is widened rather than the argument asserted.
      return (options as (string | undefined)[]).includes(fileName[index]);
    }

    // Resolve the character-count range. minCharacters/maxCharacters are the
    // current model; a lone legacy allowedCharacters is an exact length whose
    // pre-min/max semantics (length only, any characters) must be preserved.
    let minChars = rule.minCharacters;
    let maxChars = rule.maxCharacters;
    let legacyExact = false;
    if (minChars == null && maxChars == null && rule.allowedCharacters) {
      minChars = rule.allowedCharacters;
      maxChars = rule.allowedCharacters;
      legacyExact = true;
    }

    const isUnbounded = maxChars == null;
    // Free-form (any characters, may contain the separator) applies to the
    // legacy "variable" type and to a trailing text field whose max was removed.
    const isFreeForm = rule.type == "variable" || (rule.type == "text" && isUnbounded && isLastField);
    // A trailing free-form field consumes every remaining segment (e.g. "…-FRAMING PLANS").
    const value = isFreeForm && isLastField ? fileName.slice(index).join(separator) : fileName[index];

    if (isFreeForm) {
      if (!value?.trim().length) return false;
      return minChars == null || value.length >= minChars;
    }

    // Legacy exact-length rule → length only, any characters (unchanged behaviour).
    if (legacyExact) {
      return value != null && value.length === rule.allowedCharacters;
    }

    // Fully unconfigured field (no bounds) imposes no constraint.
    if (minChars == null && maxChars == null) {
      return true;
    }

    // Current model: enforce character class (number = digits, text =
    // alphanumeric) plus the configured range, and require a non-empty value.
    const pattern = rule.type == "number" ? /^[0-9]*$/ : /^[A-Za-z0-9]*$/;
    if (value == null || !pattern.test(value)) {
      return false;
    }
    const length = value.length;
    if (length == 0) {
      return false;
    }
    if (minChars != null && length < minChars) {
      return false;
    }
    if (maxChars != null && length > maxChars) {
      return false;
    }
    return true;
  });
};

export type fileUpgradeArgs = {
  resourceType: string;
  sourceFileVersions?: sourceFileArgs[];
  conversionStatus?: string;
  cachingStatus?: string;
};

export type sourceFileArgs = {
  conversionStatus?: string;
  cachingStatus?: string;
};

export const isFileVersionUpgradePossible = ({
  resourceType,
  sourceFileVersions,
  conversionStatus,
  cachingStatus,
}: fileUpgradeArgs): boolean => {
  let isUpgardePossibe = true;
  if (resourceType == "RESOURCE_TYPE_MERGED_FILE" && sourceFileVersions) {
    sourceFileVersions.forEach((file: { conversionStatus?: string; cachingStatus?: string }) => {
      if (
        (file.cachingStatus && !["COMPLETED", "NOT_REQUIRED"].includes(file.cachingStatus)) ||
        (file.conversionStatus && !["COMPLETED", "NOT_REQUIRED"].includes(file.conversionStatus))
      ) {
        isUpgardePossibe = false;
      }
    });
  } else if (
    (cachingStatus && !["COMPLETED", "NOT_REQUIRED"].includes(cachingStatus)) ||
    (conversionStatus && !["COMPLETED", "NOT_REQUIRED"].includes(conversionStatus))
  ) {
    isUpgardePossibe = false;
  }

  return isUpgardePossibe;
};

/** Unit symbols extracted from the selected display names, e.g. "Millimetre (mm)" → "mm". */
interface UnitSymbols {
  linearUnit?: string | null;
  areaUnit?: string | null;
  volumeUnit?: string | null;
}

export function updateUnits(
  props: Record<string, unknown> = {},
  unitsNew: Record<string, unknown>,
  portalUnits: PortalUnits | null | undefined,
) {
  const lengthsArray = [
    "Length",
    "Width",
    "Height",
    "Thickness",
    "Elevation",
    "Perimeter",
    "Offset",
    "Depth",
    "Distance",
    "Spacing",
  ];

  if (typeof props !== "object") return {};

  // `unitsNew` is the free-form units payload, so its members arrive as `unknown`; anything that is
  // not a display-name string simply has no symbol to extract.
  const extractSymbol = (str: unknown) => {
    if (typeof str !== "string") return null;
    const match = /.*\(([^)]+)\)/.exec(str);
    return match?.[1] || null;
  };

  const unitFactors: Record<string, number> = {};
  [...(portalUnits?.linearUnits ?? []), ...(portalUnits?.areaUnits ?? []), ...(portalUnits?.volumeUnits ?? [])].forEach(
    (portalUnit) => {
      const symbol = extractSymbol(portalUnit.unit);
      if (symbol) unitFactors[symbol] = portalUnit.factor;
    },
  );

  const units: UnitSymbols = {};
  const linearUnit = get(unitsNew, "linearUnit");
  const areaUnit = get(unitsNew, "areaUnit");
  const volumeUnit = get(unitsNew, "volumeUnit");

  if (linearUnit) units.linearUnit = extractSymbol(linearUnit);
  if (areaUnit) units.areaUnit = extractSymbol(areaUnit);
  if (volumeUnit) units.volumeUnit = extractSymbol(volumeUnit);

  return Object.fromEntries(
    Object.entries(props).map(([key, value]): [string, unknown] => {
      if (typeof value !== "string") return [key, value];

      const [numberPart, unit] = value.split(" ");
      const val = Number(numberPart);
      const resolvedUnit = unit ?? "";

      // The `!`s below only restate the truthiness guards in each `if`: TS narrows a property
      // reference but not an element access keyed by one, so the arithmetic needs them. Erased.
      if (key.includes("CUBE Area") && unitFactors[resolvedUnit] && units.areaUnit && unitFactors[units.areaUnit]) {
        const valueInMM = val * unitFactors[resolvedUnit]!;
        const newValue = valueInMM / unitFactors[units.areaUnit]!;
        return [key, `${newValue} ${units.areaUnit}`];
      } else if (
        key.includes("CUBE Volume") &&
        unitFactors[resolvedUnit] &&
        units.volumeUnit &&
        unitFactors[units.volumeUnit]
      ) {
        const valueInMM = val * unitFactors[resolvedUnit]!;
        const newValue = valueInMM / unitFactors[units.volumeUnit]!;
        return [key, `${newValue} ${units.volumeUnit}`];
      } else if (lengthsArray.some((field) => key.includes(field))) {
        const newUnit = units.linearUnit;
        return newUnit ? [key, `${val} ${newUnit}`] : [key, value];
      } else if (key.includes("Area")) {
        const newUnit = units.areaUnit;
        return newUnit ? [key, `${val} ${newUnit}`] : [key, value];
      } else if (key.includes("Volume")) {
        const newUnit = units.volumeUnit;
        return newUnit ? [key, `${val} ${newUnit}`] : [key, value];
      }

      return [key, value];
    }),
  );
}

export const getIsSocketConnected = (): boolean => {
  // `window.CubeGlobal` is declared `any` on purpose — HCViewerUtils.tsx keeps that registry open
  // because 43 modules hang 79 different members off it — so the one flag read here is narrowed at the
  // read instead. ViewerImpl.tsx / Viewer.tsx only ever assign a boolean to `socketDisconnected`, and a
  // missing registry or missing flag still reads as connected, exactly as the optional chain did.
  const registry: unknown = window.CubeGlobal;
  if (typeof registry !== "object" || registry === null || !("socketDisconnected" in registry)) {
    return true;
  }
  return !registry.socketDisconnected;
};

// KNOWN BUG: this returns `any` instead of narrowing, and it dereferences `viewerImpl.hwv` with no null
// check even though ViewerContext.ts declares `viewerImpl: … | null`.
// Impact: latent - the `&&` chain short-circuits on isModelStructureReady/isSettingsApplied/hwv and no
// provider sets those true while `viewerImpl` is still null. The type half is paid today: tsc cannot
// narrow `viewerImpl` through this signature, which is why SelectionSetItemsTable.tsx:82 casts
// HoopsViewerContext to a NonNullable-viewerImpl shape and why its `!viewerImpl` guards read as dead.
// Blocked on: making this a type predicate (`): viewerImpl is NonNullable<HoopsViewerImpl>`) here - that
// flips the result type from `any` to `boolean` for every consumer of this helper, all outside this module.
export const getIsViewerDependencyLoaded = ({
  isModelStructureReady,
  isSettingsApplied,
  viewerImpl,
  hwv,
}: {
  isModelStructureReady: boolean;
  isSettingsApplied: boolean;
  // BOTH LEFT AS `any`, and they are load-bearing. `HoopsViewerImpl` exposes `hwv` only through
  // its deliberate `[key: string]: any` index signature (ViewerImpl.tsx) and `window.CubeGlobal`
  // is untyped by design (HCViewerUtils.tsx). More importantly, this function's value is the
  // `&&` chain itself: typing either slot `unknown`/`object` turns the RETURN type from `any`
  // into `unknown`, which breaks every consumer that uses the result as a boolean. Those
  // consumers are outside this module.

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  viewerImpl: any;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  hwv: any;
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access -- see KNOWN BUG above
  return isModelStructureReady && isSettingsApplied && hwv && viewerImpl.hwv && getIsSocketConnected();
};

/**
 * Runs `tasks` with at most `maxConcurrency` in flight at once, preserving
 * input order in the returned results array. Caps the peak working set: only
 * `maxConcurrency` tasks' transient memory is alive at a time, so each task's
 * intermediates become GC-eligible as it settles (the resolved values are
 * still retained — they're the return value).
 *
 * Lives here (a leaf util) rather than in `actions/Viewer.ts` so both
 * `Viewer.ts` and `services/utils/modelPropertiesDataDB.ts` can use it without
 * a circular import. `Viewer.ts` re-exports it for existing consumers.
 */
export async function runWithConcurrency(
  tasks: Array<() => Promise<unknown>>,
  maxConcurrency = 5,
  signal?: AbortSignal,
): Promise<unknown[]> {
  const results: Promise<unknown>[] = [];
  const executing: Promise<void>[] = [];

  for (const task of tasks) {
    if (signal?.aborted) break;
    const p = task();
    results.push(p);

    const e: Promise<void> = p.then(() => {
      executing.splice(executing.indexOf(e), 1);
    });
    executing.push(e);

    if (executing.length >= maxConcurrency) {
      await Promise.race(executing);
    }
  }

  return Promise.all(results);
}

export const ObjectTreeFieldsMinValuesMapper = {
  fileId: 1,
  fileVersionId: 2,
  cubeUniqueId: 3,
  nodeId: 4,
  parent: 5,
  "CUBE Name": 6,
};
